import { message } from 'antd';
import * as React from 'react';
import { deleteTaskDetailsField, duplicateTaskDetailsField, getTaskDetails } from 'src/api/shipmentExecution';
import FieldNodeGroup from 'src/components/common/form/FieldNodeGroup';
import { FieldHookProps } from 'src/components/common/form/form.types';
import { Field } from 'src/dto/response/getTaskDetails';
import useApi from 'src/hooks/useApi';
import { useReduxState } from 'src/hooks/useRedux';
import { isValidArray } from 'src/Library/Helper';
import { getGrpFieldsProps } from '../../../SECommon/SEFieldGroupUtils';

const useSETaskDetailFields = ({ form, id, taskId, defaultDisabled, onGotoTask }) => {
  const taskDetails = useSETaskDetailFieldsInternal({ form, id, taskId, defaultDisabled, onGotoTask });
  const parentTaskDetails = useSETaskDetailFieldsInternal({
    form,
    id,
    taskId,
    defaultDisabled: true,
    prefix: 'parent_',
    onGotoTask,
  });
  const taskDetailsApi = useApi(getTaskDetails);

  const parentTasksMeta = taskDetailsApi.data?.parentTaskDetails;
  // const taskDetails = taskDetailsApi.data?.taskDetails;

  React.useEffect(() => {
    taskId && taskDetailsApi.call({ id, taskId });
  }, [taskId]);

  React.useEffect(() => {
    if (taskDetailsApi.status === 'FINISHED') {
      taskDetails.setTaskDetails(taskDetailsApi.data?.taskDetails);
      if (parentTasksMeta && isValidArray(parentTasksMeta)) {
        parentTaskDetails.setTaskDetails(parentTasksMeta[0]?.details);
      }
    }
  }, [taskDetailsApi.status]);

  const getNestedFieldValues = (
    formFields: {
      fieldId: string;
      values: any;
    }[]
  ) => {
    const findFormFieldById = (id: string) =>
      formFields.find((formField) => formField.fieldId === id);

    const resFields = [] as typeof formFields;
    const apiFields = [] as Field[];
    const parentTasksFields = taskDetailsApi.data?.parentTaskDetails?.map(
      (parent) => parent.details?.taskFields
    );
    const taskFields = taskDetailsApi.data?.taskDetails?.taskFields;
    const containerFields = taskDetailsApi.data?.taskDetails?.containers?.reduce((acc, container) => {
      acc.push(...container.fields);
      return acc;
    },[] as Field[]);
    const docFields = taskDetailsApi.data?.taskDetails?.documents?.reduce((acc, doc) => {
      acc.push(...doc.fields);
      return acc;
    },[] as Field[]);
    // parentTasksFields &&
    //   parentTasksFields.forEach((parentTaskFields) => apiFields.push(...parentTaskFields));
    taskFields && apiFields.push(...taskFields);
    docFields && apiFields.push(...docFields);
    containerFields && apiFields.push(...containerFields);
    apiFields.forEach((apiField) => {
      if (apiField.type === 'OBJECT') {
        const objectFieldValues = apiField.values?.map((value) => {
          const resField = findFormFieldById((value as Field).fieldId);
          return resField;
        });
        isValidArray(objectFieldValues) &&
          resFields.push({ fieldId: apiField.fieldId, values: objectFieldValues });
      } else {
        const resField = findFormFieldById(apiField.fieldId);
        resField && resFields.push(resField);
      }
    });
    return resFields;
  };

  const reloadModal = () => {
    onGotoTask(taskId);
  }

  return {
    taskDetails,
    reloadModal,
    parentTaskDetails,
    parentTaskMeta: parentTasksMeta && isValidArray(parentTasksMeta) ? parentTasksMeta[0] : null,
    getNestedFieldValues,
    ...taskDetailsApi,
  };
};

const useSETaskDetailFieldsInternal = ({
  form,
  id,
  taskId,
  defaultDisabled,
  prefix = '',
  onGotoTask,
}) => {
  const [taskNodes, setTaskNodes] = React.useState([] as any[]);
  const [containerNodes, setContainerNodes] = React.useState([] as any[]);
  const [awbNodes, setAwbNodes] = React.useState([] as any[]);
  const [docNodes, setDocNodes] = React.useState([] as any[]);
  const [attachments, setAttachments] = React.useState([] as any[]);
  const [taskDetails, setTaskDetails] = React.useState(null as any);
  const [noFieldToRender, setNoFieldToRender] = React.useState(false);
  const duplicateFieldApi = useApi(duplicateTaskDetailsField);
  const deleteFieldApi = useApi(deleteTaskDetailsField);

  // const { dropdownMasterValues } = taskDetails || {};
  const dropdownMasterValues = useReduxState([ 'shipmentExecutionReducer', 'dropdownMaster' ]).value;

  React.useEffect(() => {
    if (taskDetails) {
      setNodesFromAPI();
    }
  }, [taskDetails]);

  React.useEffect(() => {
    if (duplicateFieldApi.status === 'FINISHED') {
      form.validateFields(async (err, values) => {
        onGotoTask(taskId, values);
      });
      message.success('Field duplicated successfully!');
    }
    if (duplicateFieldApi.status === 'FAILED') {
      message.error('Field could not be duplicated!');
    }
  }, [duplicateFieldApi.status, duplicateFieldApi.data]);

  React.useEffect(() => {
    if (deleteFieldApi.status === 'FINISHED') {
      form.validateFields(async (err, values) => {
        onGotoTask(taskId, values);
      });
      message.success('Field deleted successfully!');
    }
    if (deleteFieldApi.status === 'FAILED') {
      message.error('Field could not be deleted!');
    }
  }, [deleteFieldApi.status, deleteFieldApi.data]);

  const duplicateField = (field: any) => {
    duplicateFieldApi.call({
      id,
      taskId,
      fieldId: field?.extraData?.fieldId,
    });
  };

  const deleteField = (field: any) => {
    deleteFieldApi.call({
      id,
      taskId,
      fieldId: field?.extraData?.fieldId,
    });
  };

  const setNodesFromAPI = () => {
    const taskFields =
      getGrpFieldsProps({
        form,
        fields: taskDetails?.taskFields,
        keyCB: (key) => `${prefix}taskFields[${key}]`,
        defaultDisabled,
        dropdownMasterValues,
      }) || [];
      
    const allContainersFields =
      taskDetails?.containers?.map((el, idx) => {
        const fieldProps =
          getGrpFieldsProps({
            form,
            fields: el.fields,
            keyCB: (key) => `${prefix}containerField[${idx}][${key}]`,
            defaultDisabled,
            dropdownMasterValues,
          }) || [];
        return getSubGroupProps(fieldProps, el);
      }) || [];

    const allAwbFields = 
      taskDetails?.awbs?.map((el, idx) => {
          const fieldProps =
            getGrpFieldsProps({
              form,
              fields: el.fields,
              keyCB: (key) => `${prefix}awbField[${idx}][${key}]`,
              defaultDisabled,
              dropdownMasterValues,
            }) || [];
          return getSubGroupProps(fieldProps, el);
        }) || [];

    const allDocumentsFields =
      taskDetails?.documents
        ?.filter((doc) => doc.documentType !== 'ATTACHMENT')
        ?.map((el, idx) => {
          const fieldProps =
            getGrpFieldsProps({
              form,
              fields: el.fields,
              keyCB: (key) => `${prefix}documentField[${idx}][${key}]`,
              defaultDisabled,
              dropdownMasterValues,
            }) || [];
          return getSubGroupProps(fieldProps, el);
        }) || [];
    setTaskNodes([...taskFields]);
    setContainerNodes([...allContainersFields]);
    setAwbNodes([...allAwbFields]);
    setDocNodes([...allDocumentsFields]); // allDocumentsFields
    
    /** Checking for attachments before changing NoFieldToRender (p.s. not a part of this function) */
    const attachmentFields = taskDetails?.documents?.filter((doc) => doc.documentType === 'ATTACHMENT') || [];
    setAttachments([...attachmentFields]);

    setNoFieldToRender(!(taskFields?.length || allContainersFields?.length || allDocumentsFields?.length || attachmentFields?.length));
  };

  const getSubGroupProps = (defaultNodes: any[], extraData?: any) => {
    return {
      renderComponent: FieldNodeGroup(defaultNodes),
      renderProps: {
        extraData,
      } as FieldHookProps,
    };
  };

  return {
    taskNodes,
    containerNodes,
    awbNodes,
    docNodes,
    attachments,
    dropdownMasterValues,
    setTaskDetails,
    duplicateField,
    deleteField,
    noFieldToRender,
  };
};

export default useSETaskDetailFields;
